export interface CollectedFile {
  file: File;
  relativePath: string;
}

export function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const allEntries: FileSystemEntry[] = [];
    const readBatch = () => {
      reader.readEntries(
        (entries) => {
          if (entries.length === 0) {
            resolve(allEntries);
          } else {
            allEntries.push(...entries);
            readBatch();
          }
        },
        reject,
      );
    };
    readBatch();
  });
}

export async function collectFilesFromEntry(
  entry: FileSystemEntry,
  basePath: string = '',
): Promise<{ files: CollectedFile[]; emptyFolderPaths: string[] }> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await new Promise<File>((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
    return { files: [{ file, relativePath: basePath + entry.name }], emptyFolderPaths: [] };
  }

  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    const entries = await readAllEntries(reader);
    const dirPath = basePath + entry.name + '/';
    const allFiles: CollectedFile[] = [];
    const allEmptyPaths: string[] = [];

    if (entries.length === 0) {
      allEmptyPaths.push(dirPath);
    } else {
      for (const child of entries) {
        const result = await collectFilesFromEntry(child, dirPath);
        allFiles.push(...result.files);
        allEmptyPaths.push(...result.emptyFolderPaths);
      }
    }
    return { files: allFiles, emptyFolderPaths: allEmptyPaths };
  }

  return { files: [], emptyFolderPaths: [] };
}

export function extractFolderPaths(relativePaths: string[]): string[] {
  const folderSet = new Set<string>();
  for (const rp of relativePaths) {
    const parts = rp.replace(/\/$/, '').split('/');
    // For file paths, build all ancestor folder paths
    // For folder paths (ending with /), include the path itself
    const isFolder = rp.endsWith('/');
    const depth = isFolder ? parts.length : parts.length - 1;
    for (let i = 1; i <= depth; i++) {
      folderSet.add(parts.slice(0, i).join('/'));
    }
  }
  return Array.from(folderSet).sort((a, b) => {
    return a.split('/').length - b.split('/').length;
  });
}
